// triggers.ts — Triggers, line links and automatic length (book REGLES_DEVIS §7, §12).
//
// Triggers: hourly cutting (CUT-1510, "hourly" trigger → SRV-1705 service line, with degressive sale
// AND purchase rate tables on the quote total) and per-metre finishing (CONF-1613, "ml" trigger →
// per-metre accessory line). Automatic length of linked per-metre lines follows the parent line
// format. Linked secondary lines display as "N-1"; each linked set keeps a stable colour.

import { AUTODIM, CATALOGUE, CUTS, FINISHINGS } from "../../data";
import { type QuoteLine, type AutoDimFormula, num, round2 } from "../core/core";
import { engineMessage } from "../core/messages";
import { materialFamilyOf } from "../catalogue/compatibility";

// Line triggers

/**
 * Trigger of a cut: "hourly" for CUT-1510 (opens a service line), otherwise null.
 * @param code - Cut reference.
 * @returns The trigger code, or `null`.
 */
export function cutTrigger(code: string | undefined): string | null {
  return CUTS.find((c) => c.code === code)?.trigger ?? null;
}

/**
 * Trigger of a finishing: "ml" for CONF-1613 (opens a per-metre accessory line), otherwise null.
 * @param code - Finishing reference.
 * @returns The trigger code, or `null`.
 */
export function finishingTrigger(code: string | undefined): string | null {
  return FINISHINGS.find((c) => c.code === code)?.trigger ?? null;
}

// Hourly cutting (SRV-1705) — degressive rate tables on cumulated hours

/**
 * SALE rate €/h of hourly cutting by cumulated hours: 185 (<3), 155 (≥3), 135 (≥10), 120 (≥20).
 * @param hours - Cumulated hours.
 * @returns The sale rate in €/h.
 */
export function svcCutPvRate(hours: number): number {
  return hours >= 20 ? 120 : hours >= 10 ? 135 : hours >= 3 ? 155 : 185;
}

/**
 * PURCHASE cost €/h of hourly cutting by cumulated hours: 100 (<3), 80 (≥3), 60 (≥10).
 * @param hours - Cumulated hours.
 * @returns The purchase cost in €/h.
 */
export function svcCutPaRate(hours: number): number {
  return hours >= 10 ? 60 : hours >= 3 ? 80 : 100;
}

// Automatic length (sandow rule)

/**
 * Automatic length formula attached to an accessory/finishing reference, or undefined.
 * @param ref - Accessory or finishing reference.
 * @returns The formula, or `undefined`.
 */
export function autoDimFor(ref: string | undefined): AutoDimFormula | undefined {
  const formula = ref != null ? AUTODIM[ref] : undefined;
  return formula as AutoDimFormula | undefined;
}

/**
 * Length (linear metres) of a linked per-metre line, derived from the parent line format and the
 * formula: perimeter = unit perimeter; double_perimeter = perimeter × 2 (sandow); width = width; double_width = width × 2
 * (top+bottom pocket). Multiplied by the parent quantity, rounded to 2 decimals.
 * @param parent - Parent line carrying the format.
 * @param formula - Automatic length formula.
 * @returns The length in linear metres.
 */
export function autoMeterage(parent: QuoteLine, formula: AutoDimFormula): number {
  const perimeterUnit = (2 * (num(parent.L) + num(parent.H))) / 1000;
  const widthUnit = num(parent.L) / 1000;
  const parentQuantity = Math.max(1, (num(parent.fileQty) || 1) * (num(parent.copiesPerFile) || 1));
  const bases: Record<AutoDimFormula, number> = {
    perimeter: perimeterUnit,
    double_perimeter: 2 * perimeterUnit,
    width: widthUnit,
    double_width: 2 * widthUnit,
  };
  return round2(bases[formula] * parentQuantity);
}

// Pole pockets — diameter mandatory

const SLEEVE_CODES = new Set([
  "CONF-1632",
  "CONF-1633",
  "CONF-1634",
  "CONF-1635",
  "CONF-1636",
  "CONF-1637",
]);

/**
 * Does the line carry a pole pocket (in any finishing slot finishing1..finishing6, or as its reference)?
 * @param line - Line to test.
 * @returns True when the line carries a pole pocket.
 */
export function isSleeve(line: QuoteLine): boolean {
  return [line.finishing1, line.finishing2, line.finishing3, line.finishing4, line.finishing5, line.finishing6, line.ref].some((c) => c != null && SLEEVE_CODES.has(c));
}

/**
 * A pole pocket with no diameter blocks validation (Ø column is mandatory).
 * @param line - Line to test.
 * @returns True when the line must be blocked.
 */
export function sleeveWithoutDiameter(line: QuoteLine): boolean {
  return isSleeve(line) && !(line.diam != null && String(line.diam).trim() !== "");
}

// PRINT MODE — different recto-verso & mirror.
//
// Two PRODUCTION FLAGS, checkable per line, with ZERO pricing effect: they touch neither the sale
// price, nor the purchase price, nor the minimums, nor the tier base. They are only checkable in
// their applicable case and greyed out elsewhere — same pattern as `isSleeve` above.

/** The §17 families whose transparency makes MIRROR printing useful.
 *  BY FAMILY, never by reference: a transparent material added tomorrow inherits the rule without
 *  code changes. `PLX_DIFF` is EXCLUDED — a mirror on a diffusing substrate makes no optical sense. */
const MIRROR_FAMILIES = new Set(["AD_TRANSP", "PLX_INCOLORE"]);

/**
 * Is the material double-sided (recto-verso)? Enables the "RV DIFF" checkbox, whatever the line (print, stand...):
 * only the MATERIAL decides, not the print mode. The catalogue has no flag for it, the name says it (`RV`,
 * `recto-verso`).
 * @param ref - Material reference.
 * @returns True when the material is double-sided.
 */
export function isRectoVersoMaterial(ref: string | null | undefined): boolean {
  const material = ref ? CATALOGUE.find((m) => m.ref === ref) : undefined;
  return !!material && /\bRV\b|recto[\s-]?verso/i.test(`${material.name} ${material.fullName}`);
}

/**
 * Does the line material allow mirror printing? Enables the "MIROIR" checkbox.
 * @param line - Line to test.
 * @returns True when mirror printing is allowed.
 */
export function materialAllowsMirror(line: QuoteLine): boolean {
  const fam = materialFamilyOf(line.material);
  return fam != null && MIRROR_FAMILIES.has(fam);
}

// Display numbering and link colours

/**
 * DISPLAY numbers (by line id). The secondary line of a link (the one following its partner in the
 * table) takes "N-1" and consumes no number: linking 6 to 5 turns it into "5-1" and the former 7
 * becomes 6. `num` stays the stable internal key; only the display changes.
 * @param lines - All quote lines.
 * @returns The display number of each line, by line id.
 */
export function displayNumMap(lines: readonly QuoteLine[]): Map<number, string> {
  const map = new Map<number, string>();
  let pos = 0;
  lines.forEach((line, i) => {
    if (line.id == null) return;
    const partner = line.linkId != null ? lines.find((x) => x.id === line.linkId) : undefined;
    if (partner != null && partner.id != null && lines.indexOf(partner) < i) {
      // Secondary of a linked set: parent number + "-1".
      const parentNumber = partner.num != null ? String(partner.num) : (map.get(partner.id) ?? "?");
      map.set(line.id, parentNumber + "-1");
    } else {
      // Primary: its PERSISTENT number (l.num — moving a line does not renumber).
      // Positional fallback when num is missing (legacy fixtures/imports).
      pos++;
      map.set(line.id, line.num != null ? String(line.num) : String(pos));
    }
  });
  return map;
}

/**
 * Recompacts the line order into SETS: each secondary linked line is moved right after its parent
 * (the primary being the first of the pair in the current order). The relative order of parents and
 * standalone lines is preserved. This is the "Renumber" operation, and it has no pricing effect
 * (tiers accumulate per material over the whole quote, regardless of order).
 * @param lines - All quote lines.
 * @returns The lines re-ordered by linked set.
 */
export function orderSets<T extends { id?: number; linkId?: number | null }>(lines: readonly T[]): T[] {
  const out: T[] = [];
  const placed = new Set<number>();
  for (const line of lines) {
    if (line.id != null && placed.has(line.id)) continue; // already pulled in as child of an earlier parent
    out.push(line);
    if (line.id != null) placed.add(line.id);
    if (line.linkId != null) {
      const child = lines.find((x) => x.id === line.linkId);
      if (child && child.id != null && !placed.has(child.id)) {
        out.push(child);
        placed.add(child.id);
      }
    }
  }
  return out;
}

/**
 * "Renumber all lines": reassigns the PERSISTENT `num` = 1, 2, 3… to PRIMARY lines in display order
 * (linked secondaries follow as "N-1", with no number of their own) and recompacts the sets via
 * orderSets. This is the ONLY operation that changes numbers; moving a line does not.
 * @param lines - All quote lines.
 * @returns The lines with fresh persistent numbers.
 */
export function renumberLines<T extends { id?: number; num?: number; linkId?: number | null }>(
  lines: readonly T[],
): T[] {
  const ordered = orderSets(lines);
  let n = 0;
  return ordered.map((line) => {
    const partner = line.linkId != null ? ordered.find((x) => x.id === line.linkId) : undefined;
    const isSecondary = partner != null && ordered.indexOf(partner) < ordered.indexOf(line);
    if (isSecondary) return line; // no number of its own: follows its parent ("N-1")
    n++;
    return { ...line, num: n };
  });
}

/**
 * EFFECTIVE file name of a line: the name ENTERED by hand (never overwritten), otherwise the auto
 * "File X" where X is the DISPLAYED (persistent) number. A deliberate renumbering updates AUTO
 * names only; hand-entered names are left alone.
 * @param fileName - File name entered by hand, if any.
 * @param displayNum - Displayed line number.
 * @returns The effective file name.
 */
export function displayFileName(fileName: string | undefined, displayNum: string): string {
  const enteredName = fileName?.trim();
  return enteredName ? enteredName : engineMessage("engineFileFallback", { num: displayNum });
}

// Auto-incremented production notes (cross-reference prefix)

/**
 * Link origin identity keys (language-free). Stored on `linkLabel`; the French wording lives in fr.json
 * and is written into `prodNote` by {@link applyLinkRefs} via {@link engineMessage}.
 */
export const LINK_LABELS = {
  manual: "engineLinkManual",
  finishingMlParent: "engineLinkFinishingMlParent",
  finishingMlChild: "engineLinkFinishingMlChild",
  serviceParent: "engineLinkServiceParent",
  serviceChild: "engineLinkServiceChild",
  /**
   * CUSTOM-SIZE extra (§9) — a parent/child PAIR, like the two triggers above. The parent
   * carries `customSizeParent`, the child `customSizeChild`: giving the CHILD label to the parent would
   * make it look auto-created and get it swept away by the §7 cascade and symmetry.
   */
  customSizeParent: "engineLinkCustomSizeParent",
  customSizeChild: "engineLinkCustomSizeChild",
} as const;

/** Legacy French `linkLabel` values (trailing space) kept so old drafts still cascade correctly. */
const LEGACY_LINK_LABELS = {
  serviceChild: "Découpe machine pour la ligne ",
  finishingMlChild: "Confection au ml de la ligne ",
  customSizeChild: "Supplément sur-mesure de la ligne ",
  serviceParent: "Découpe à l'heure → ligne ",
  finishingMlParent: "Confection au ml → ligne ",
  customSizeParent: "Supplément sur-mesure → ligne ",
  manual: "Lié à la ligne ",
} as const;

/**
 * Whether `linkLabel` matches a current identity key or a legacy French prefix for the same role.
 * @param label - Stored link label.
 * @param key - Current identity key.
 * @param legacy - Legacy French prefix (with trailing space).
 * @returns True when the label identifies that role.
 */
function linkLabelIs(label: string | null | undefined, key: string, legacy: string): boolean {
  return label === key || label === legacy || (!!label && label.startsWith(legacy));
}

/**
 * Child AUTO-CREATED by a trigger (hourly cutting → SVC, per-metre finishing → per-metre line). The
 * ORIGIN MARKER is the `linkLabel` set at insertion — MANUAL linking sets `LINK_LABELS.manual`, so a
 * hand-entered line later linked on purpose is NEVER a trigger child (§7: the deletion cascade keys
 * on the link plus its origin, not on the line type).
 * @param line - Line to test.
 * @returns True when the line was auto-created by a trigger.
 */
export function isTriggerChild(line: Pick<QuoteLine, "linkLabel">): boolean {
  return linkLabelIs(line.linkLabel, LINK_LABELS.serviceChild, LEGACY_LINK_LABELS.serviceChild)
    || linkLabelIs(line.linkLabel, LINK_LABELS.finishingMlChild, LEGACY_LINK_LABELS.finishingMlChild)
    // The CUSTOM-SIZE extra is one: it never exists alone (§4). Deleting its parent removes it,
    // with no dedicated confirmation, and it cannot be unlinked to survive on its own.
    || linkLabelIs(line.linkLabel, LINK_LABELS.customSizeChild, LEGACY_LINK_LABELS.customSizeChild);
}

/**
 * Does the PARENT still carry the trigger that created this child? Data-driven (never a hard-coded
 * ref): serviceChild ⇔ a cut with the "hourly" trigger; finishingMlChild ⇔ a finishing with the "ml"
 * trigger. Serves §7 SYMMETRY: unticking the trigger on the parent removes its auto-created child.
 * @param child - Auto-created child line.
 * @param parent - Its parent line.
 * @returns True when the parent still justifies the child.
 */
export function parentHasTriggerFor(child: Pick<QuoteLine, "linkLabel">, parent: QuoteLine): boolean {
  /**
   * Normalises a field value to a code, or `undefined` when empty.
   * @param v - Raw value.
   * @returns The code, or `undefined`.
   */
  const code = (v: unknown): string | undefined => (v == null || v === "" ? undefined : String(v));
  if (linkLabelIs(child.linkLabel, LINK_LABELS.serviceChild, LEGACY_LINK_LABELS.serviceChild)) {
    return cutTrigger(code(parent.cut)) === "hourly";
  }
  if (linkLabelIs(child.linkLabel, LINK_LABELS.finishingMlChild, LEGACY_LINK_LABELS.finishingMlChild)) {
    return [parent.finishing1, parent.finishing2, parent.finishing3, parent.finishing4, parent.finishing5, parent.finishing6].some((c) => finishingTrigger(code(c)) === "ml");
  }
  // The CUSTOM-SIZE extra comes from a HUMAN DECISION (an entered format), not from a component
  // ticked on the parent, so it is NEVER obsolete: it stays alive as long as its parent exists and
  // any incompatibility surfaces as a validation block, never as a silent deletion.
  if (linkLabelIs(child.linkLabel, LINK_LABELS.customSizeChild, LEGACY_LINK_LABELS.customSizeChild)) return true;
  return true; // not a trigger child: never "obsolete"
}

/** Encoded `engineLink*` prodNote prefix, or a legacy French cross-reference prefix. */
const LINK_REF_RE =
  /^(?:engineLink(?:Manual|FinishingMlParent|FinishingMlChild|ServiceParent|ServiceChild|CustomSizeParent|CustomSizeChild)(?:\|\{.*\})?|(?:Lié à la ligne|Confection au ml → ligne|Confection au ml de la ligne|Découpe à l'heure → ligne|Découpe machine pour la ligne|Supplément sur-mesure → ligne|Supplément sur-mesure de la ligne)[^\n]*)\n?/;

/**
 * Resolves a stored `linkLabel` (current key or legacy French) to the identity key used in prodNote.
 * @param label - Stored link label.
 * @returns An `engineLink*` key.
 */
function resolveLinkLabelKey(label: string | null | undefined): string {
  if (label && (Object.values(LINK_LABELS) as string[]).includes(label)) return label;
  for (const [role, legacy] of Object.entries(LEGACY_LINK_LABELS) as [keyof typeof LINK_LABELS, string][]) {
    if (linkLabelIs(label, LINK_LABELS[role], legacy)) return LINK_LABELS[role];
  }
  return LINK_LABELS.manual;
}
/**
 * Sets (or removes, when `texte` is empty) the cross-reference prefix at the head of the production
 * note, PRESERVING the free text below. Idempotent: an existing prefix is stripped first.
 * @param prodNote - Current production note.
 * @param text - New prefix (empty to remove).
 * @returns The production note with the prefix applied.
 */
export function setLinkPrefix(prodNote: string | undefined, text: string): string {
  const freeText = (prodNote ?? "").replace(LINK_REF_RE, "");
  return text ? text + (freeText ? "\n" + freeText : "") : freeText;
}

/**
 * Refreshes the cross-reference prefixes of ALL lines from the current DISPLAY numbers: every linked
 * line gets "{linkLabel}{partner display number}" at the head of its production note, while an
 * unlinked line (or one whose partner vanished) has any leftover prefix removed. Free text is always
 * preserved. Call after any change to link structure or numbering.
 * @param lines - All quote lines.
 * @returns The lines with refreshed cross-reference prefixes.
 */
export function applyLinkRefs(lines: readonly QuoteLine[]): QuoteLine[] {
  const displayNums = displayNumMap(lines);
  return lines.map((line) => {
    if (line.linkId != null) {
      const partner = lines.find((x) => x.id === line.linkId);
      if (partner != null && partner.id != null) {
        const partnerNumber = displayNums.get(partner.id) ?? (partner.num != null ? String(partner.num) : "?");
        const prefix = engineMessage(resolveLinkLabelKey(line.linkLabel), { line: partnerNumber });
        const note = setLinkPrefix(line.prodNote, prefix);
        return note === line.prodNote ? line : { ...line, prodNote: note };
      }
      // Partner not found: untie the link and clean up the prefix.
      return { ...line, linkId: null, linkLabel: null, prodNote: setLinkPrefix(line.prodNote, "") };
    }
    // Standalone line: remove any leftover prefix (after an unlink).
    const note = setLinkPrefix(line.prodNote, "");
    return note === (line.prodNote ?? "") ? line : { ...line, prodNote: note };
  });
}

/**
 * Colour (HSL) per linked set, keyed by the sorted id pair. Each group gets a hue from its order of
 * appearance (cycle of 8), so two neighbouring sets never share a colour.
 * @param lines - All quote lines.
 * @returns The HSL colour by linked set key.
 */
export function linkColorMap(lines: readonly QuoteLine[]): Map<string, string> {
  const hues = [18, 200, 140, 280, 330, 48, 260, 170];
  const map = new Map<string, string>();
  let groupIndex = 0;
  for (const line of lines) {
    if (line.linkId == null || line.id == null) continue;
    const key = Math.min(line.id, line.linkId) + "-" + Math.max(line.id, line.linkId);
    if (!map.has(key)) {
      map.set(key, `hsl(${hues[groupIndex % hues.length]},70%,52%)`);
      groupIndex++;
    }
  }
  return map;
}
